import { useState, useEffect } from "react";
import { Stethoscope, Activity, Clock, FileText, Check, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const MODALITY_OPTIONS = [
  "Manual Therapy",
  "Exercise Therapy",
  "IFT",
  "TENS",
  "Ultrasound",
  "Dry Needling",
  "Traction",
  "Heat Therapy",
  "Cold Therapy",
  "Taping",
  "Cupping",
  "Other",
];

const DURATION_PRESETS = [15, 30, 45, 60];

export default function TreatmentRecordDialog({
  open,
  onOpenChange,
  attendance,
  patient,
  onSaved,
}) {
  const { user } = useAuth();
  const [physios, setPhysios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    session_id: null,
    modalities: [],
    duration: 30,
    pain_score_before: "",
    pain_score_after: "",
    mobility_note: "",
    therapist_notes: "",
    next_instruction: "",
    therapist_id: "",
  });

  useEffect(() => {
    if (open) {
      api.get("/users/physiotherapists").then((r) => setPhysios(r.data || [])).catch(() => {});
      
      const patId = patient?.id || attendance?.patient_id;
      const attId = attendance?.id;

      // Check if a treatment record already exists for this attendance
      if (attId) {
        setLoading(true);
        api.get("/treatment-sessions", { params: { attendance_id: attId } })
          .then((r) => {
            const items = r.data.items || [];
            if (items.length > 0) {
              const rec = items[0];
              setForm({
                session_id: rec.id,
                modalities: rec.modalities || [],
                duration: rec.duration || 30,
                pain_score_before: rec.pain_score_before ?? "",
                pain_score_after: rec.pain_score_after ?? (rec.pain_score ?? ""),
                mobility_note: rec.mobility_note || "",
                therapist_notes: rec.therapist_notes || "",
                next_instruction: rec.next_instruction || "",
                therapist_id: rec.therapist_id || user?.id || "",
              });
            } else {
              setForm({
                session_id: null,
                modalities: ["Manual Therapy"],
                duration: 30,
                pain_score_before: "",
                pain_score_after: "",
                mobility_note: "",
                therapist_notes: "",
                next_instruction: "",
                therapist_id: user?.id || "",
              });
            }
          })
          .catch(() => {})
          .finally(() => setLoading(false));
      } else {
        setForm({
          session_id: null,
          modalities: ["Manual Therapy"],
          duration: 30,
          pain_score_before: "",
          pain_score_after: "",
          mobility_note: "",
          therapist_notes: "",
          next_instruction: "",
          therapist_id: user?.id || "",
        });
      }
    }
  }, [open, attendance, patient, user]);

  const toggleModality = (m) => {
    setForm((f) => ({
      ...f,
      modalities: f.modalities.includes(m)
        ? f.modalities.filter((x) => x !== m)
        : [...f.modalities, m],
    }));
  };

  const handleSave = async () => {
    const patId = patient?.id || attendance?.patient_id;
    if (!patId) {
      toast.error("Patient ID not found");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        patient_id: patId,
        attendance_id: attendance?.id || null,
        attendance_date: attendance?.date || null,
        service_type: attendance?.service || "clinic",
        modalities: form.modalities,
        duration: parseInt(form.duration) || 30,
        pain_score_before: form.pain_score_before !== "" ? parseInt(form.pain_score_before) : null,
        pain_score_after: form.pain_score_after !== "" ? parseInt(form.pain_score_after) : null,
        mobility_note: form.mobility_note,
        therapist_notes: form.therapist_notes,
        next_instruction: form.next_instruction,
        therapist_id: form.therapist_id || user?.id,
      };

      if (form.session_id) {
        await api.put(`/treatment-sessions/${form.session_id}`, payload);
        toast.success("Treatment record updated successfully");
      } else {
        await api.post("/treatment-sessions", payload);
        toast.success("Treatment record saved successfully");
      }

      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(apiError(e, "Failed to save treatment record"));
    } finally {
      setSaving(false);
    }
  };

  const patName = patient?.name || attendance?.patient_name || "Patient";
  const patCode = patient?.patient_id || attendance?.patient_code || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-bk-blue font-display">
            <Stethoscope className="h-5 w-5 text-bk-blue" />
            Treatment Record — <span className="font-semibold text-slate-800">{patName}</span>
            {patCode && <span className="text-xs text-slate-400 font-normal">({patCode})</span>}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-bk-blue" />
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Modalities Multi-Select */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Treatment Modalities
              </Label>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {MODALITY_OPTIONS.map((m) => {
                  const selected = form.modalities.includes(m);
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => toggleModality(m)}
                      className={`px-3 py-1 text-xs rounded-full border transition-all ${
                        selected
                          ? "bg-bk-blue text-white border-bk-blue font-medium shadow-xs"
                          : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      {selected && <Check className="h-3 w-3 inline mr-1" />}
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Pain Scores & Duration Grid */}
            <div className="grid grid-cols-3 gap-3 bg-slate-50 p-3 rounded-lg border border-slate-100">
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Pain Before (0–10)</Label>
                <Input
                  type="number"
                  min="0"
                  max="10"
                  placeholder="e.g. 8"
                  value={form.pain_score_before}
                  onChange={(e) => setForm({ ...form, pain_score_before: e.target.value })}
                  className="h-9 bg-white"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Pain After (0–10)</Label>
                <Input
                  type="number"
                  min="0"
                  max="10"
                  placeholder="e.g. 4"
                  value={form.pain_score_after}
                  onChange={(e) => setForm({ ...form, pain_score_after: e.target.value })}
                  className="h-9 bg-white font-semibold text-bk-green"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Duration (Mins)</Label>
                <Input
                  type="number"
                  min="5"
                  step="5"
                  value={form.duration}
                  onChange={(e) => setForm({ ...form, duration: e.target.value })}
                  className="h-9 bg-white"
                />
              </div>
            </div>

            {/* Duration Presets */}
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <span>Quick Duration:</span>
              {DURATION_PRESETS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setForm({ ...form, duration: d })}
                  className={`px-2 py-0.5 rounded text-xs border ${
                    parseInt(form.duration) === d
                      ? "bg-bk-lightBlue text-bk-blue border-blue-200 font-semibold"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {d}m
                </button>
              ))}
            </div>

            {/* Mobility & Progress Notes */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Mobility / Progress Note</Label>
              <Textarea
                placeholder="e.g. Cervical range of motion improved by 20%, SLR increased to 60 deg..."
                rows={2}
                value={form.mobility_note}
                onChange={(e) => setForm({ ...form, mobility_note: e.target.value })}
                className="text-sm"
              />
            </div>

            {/* Therapist Notes & Clinical Instructions */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Therapist Notes / Instructions</Label>
              <Textarea
                placeholder="e.g. Advised ice pack twice daily, core strengthening exercises, avoid forward bending..."
                rows={2}
                value={form.therapist_notes}
                onChange={(e) => setForm({ ...form, therapist_notes: e.target.value })}
                className="text-sm"
              />
            </div>

            {/* Physiotherapist */}
            {physios.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Attending Physiotherapist</Label>
                <Select
                  value={form.therapist_id || ""}
                  onValueChange={(v) => setForm({ ...form, therapist_id: v })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select physiotherapist" />
                  </SelectTrigger>
                  <SelectContent>
                    {physios.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            className="bg-bk-blue hover:bg-blue-700 text-white gap-1.5"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {form.session_id ? "Update Treatment" : "Save Treatment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
